/**
 * Bundle builder — choose a model, then build a box from that model's collection.
 * No framework, no theme dependencies.
 */
class BundleBuilder extends HTMLElement {
  connectedCallback() {
    this.boxSize = parseInt(this.dataset.boxSize, 10) || 6;
    this.bundleName = this.dataset.bundleName || 'Build Your Own Box';
    this.discount = parseFloat(this.dataset.discount) || 0;
    this.cartUrl = this.dataset.cartUrl || '/cart';
    this.goToCart = this.dataset.goToCart === 'true';
    this.remember = this.dataset.remember === 'true';
    this.giftEnabled = this.dataset.giftNote === 'true';
    this.giftRequired = this.dataset.giftRequired === 'true';
    this.giftMax = parseInt(this.dataset.giftMax, 10) || 160;
    this.giftDisplay = this.dataset.giftDisplay || 'single';
    this.giftLabel = this.dataset.giftLabel || 'Gift card';
    this.searchThreshold = parseInt(this.dataset.searchThreshold, 10) || 8;
    this.allBrandsLabel = this.dataset.allBrands || 'All';

    const raw = this.querySelector('[data-bundle-data]');
    if (!raw) return;
    try { this.products = JSON.parse(raw.textContent); } catch (e) { return; }

    this.money = new Intl.NumberFormat(this.dataset.locale || 'en-IN', {
      style: 'currency',
      currency: this.dataset.currency || 'INR',
      maximumFractionDigits: 2
    });

    this.stepModels = this.querySelector('[data-step-models]');
    this.seekEl = this.querySelector('[data-seek]');
    this.searchEl = this.querySelector('[data-model-search]');
    this.brandsEl = this.querySelector('[data-brands]');
    this.noResultsEl = this.querySelector('[data-noresults]');
    this.modelBtns = Array.from(this.querySelectorAll('[data-model][data-model-name]'));
    this.stepBuild = this.querySelector('[data-step-build]');
    this.modelNameEl = this.querySelector('[data-current-model]');
    this.cards = Array.from(this.querySelectorAll('[data-card]'));
    this.cards.forEach((card, i) => {
      card.dataset.cardId = 'c' + i;
      card.dataset.leadId = card.dataset.productId;
    });
    this.barEl = this.querySelector('[data-bar]');
    this.trayEl = this.querySelector('[data-tray]');
    this.statusEl = this.querySelector('[data-status]');
    this.moneyEl = this.querySelector('[data-money]');
    this.wasEl = this.querySelector('[data-was]');
    this.nowEl = this.querySelector('[data-now]');
    this.saveEl = this.querySelector('[data-save]');
    this.submitEl = this.querySelector('[data-submit]');
    this.submitLabelEl = this.querySelector('[data-submit-label]');
    this.errorEl = this.querySelector('[data-error]');
    this.giftToEl = this.querySelector('[data-gift-to]');
    this.giftFromEl = this.querySelector('[data-gift-from]');
    this.giftMsgEl = this.querySelector('[data-gift-message]');
    this.giftCountEl = this.querySelector('[data-gift-count]');

    this.model = null;
    this.modelName = '';
    this.picks = [];

    this.buildChooser();
    this.bindEvents();
    this.restore();
  }

  read(k) { try { return localStorage.getItem(k); } catch (e) { return null; } }
  write(k, v) { try { localStorage.setItem(k, v); } catch (e) {} }

  restore() {
    if (!this.remember) return;
    const saved = this.read('bb:model');
    if (!saved) return;
    const btn = this.querySelector(`[data-model="${CSS.escape(saved)}"]`);
    if (btn) this.chooseModel(saved, btn.dataset.modelName, false);
  }

  /* ---------- step 1 chooser ---------- */

  buildChooser() {
    if (!this.seekEl || this.modelBtns.length < this.searchThreshold) return;

    const brands = Array.from(new Set(
      this.modelBtns.map((b) => (b.dataset.brand || '').trim()).filter(Boolean)
    )).sort((a, b) => a.localeCompare(b));

    if (brands.length > 1 && this.brandsEl) {
      this.brandsEl.innerHTML =
        `<button type="button" class="bb__brand is-active" data-brand-filter="">${this.escape(this.allBrandsLabel)}</button>` +
        brands.map((b) => `<button type="button" class="bb__brand" data-brand-filter="${this.escape(b)}">${this.escape(b)}</button>`).join('');
    }

    this.seekEl.hidden = false;
    this.chooserReady = true;
    this.brandFilter = '';
    this.query = '';
  }

  filterModels() {
    if (!this.chooserReady) return;
    const q = this.query.trim().toLowerCase();
    let visible = 0;

    this.modelBtns.forEach((btn) => {
      const brandOk = !this.brandFilter || (btn.dataset.brand || '') === this.brandFilter;
      const textOk = !q || (btn.dataset.search || '').includes(q);
      const show = brandOk && textOk;
      btn.closest('li').hidden = !show;
      if (show) visible++;
    });

    if (this.noResultsEl) this.noResultsEl.hidden = visible > 0;
  }

  /* ---------- step control ---------- */

  chooseModel(id, name, scroll = true) {
    this.model = id;
    this.modelName = name || '';
    this.picks = [];

    if (this.remember) this.write('bb:model', id);
    if (this.modelNameEl) this.modelNameEl.textContent = this.modelName;

    this.cards.forEach((card) => { card.hidden = card.dataset.model !== id; });

    this.stepModels.hidden = true;
    this.stepBuild.hidden = false;
    this.barEl.hidden = false;

    this.resolveVariants();
    this.render();

    if (scroll) this.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  backToModels() {
    this.model = null;
    this.picks = [];
    if (this.remember) this.write('bb:model', '');
    this.stepBuild.hidden = true;
    this.barEl.hidden = true;
    this.stepModels.hidden = false;
    this.clearError();
    this.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  /* ---------- variants ---------- */

  /** Returns { product, variant } for whatever the card currently has selected. */
  currentFor(card) {
    const select = card.querySelector('[data-variant-select]');

    if (select && select.selectedOptions.length) {
      const opt = select.selectedOptions[0];
      const pid = opt.dataset.product || card.dataset.leadId || card.dataset.productId;
      const product = this.products.find((p) => String(p.id) === String(pid));
      if (!product) return { product: null, variant: null };
      const variant = product.variants.find((v) => String(v.id) === select.value) || null;
      return { product, variant };
    }

    const product = this.products.find((p) => String(p.id) === card.dataset.productId);
    if (!product) return { product: null, variant: null };
    const variant = product.variants.find((v) => v.available) || product.variants[0] || null;
    return { product, variant };
  }

  resolveVariants() {
    this.cards.forEach((card) => {
      if (card.hidden) return;
      if (!card.dataset.leadId) card.dataset.leadId = card.dataset.productId;

      const { product: current, variant } = this.currentFor(card);
      const productId = card.dataset.productId;

      const pick = this.picks.find((p) => String(p.cardId) === card.dataset.cardId);
      if (pick && variant && current) {
        pick.productId = current.id;
        pick.variantId = variant.id;
        pick.cents = variant.cents;
        pick.title = current.title;
        pick.image = variant.image || current.image || pick.image;
      }

      // The card now stands for whichever product the dropdown points at.
      if (current) card.dataset.productId = current.id;
      card.dataset.variantId = variant ? variant.id : '';
      const blocked = !!variant && !variant.available;
      card.dataset.unavailable = String(blocked);

      const priceEl = card.querySelector('[data-price]');
      if (priceEl && variant) priceEl.textContent = variant.price;

      // Swap the card photo to match the chosen variant (colour, material, finish).
      const img = card.querySelector('[data-card-image]');
      if (img && variant) {
        const nextSrc = variant.card || img.dataset.defaultSrc;
        const nextSet = variant.srcset || img.dataset.defaultSrcset || '';
        if (img.getAttribute('src') !== nextSrc) {
          img.setAttribute('src', nextSrc);
          if (nextSet) img.setAttribute('srcset', nextSet);
          else img.removeAttribute('srcset');
        }
      }

      const soldout = card.querySelector('[data-soldout]');
      if (soldout) soldout.hidden = !blocked;
    });
  }

  /* ---------- events ---------- */

  bindEvents() {
    if (this.giftMsgEl && this.giftCountEl) {
      this.giftMsgEl.addEventListener('input', () => {
        this.giftCountEl.textContent = this.giftMsgEl.value.length;
      });
    }

    this.addEventListener('change', (e) => {
      if (!e.target.matches('[data-variant-select]')) return;
      this.resolveVariants();
      this.render();
    });

    if (this.searchEl) {
      this.searchEl.addEventListener('input', () => {
        this.query = this.searchEl.value;
        this.filterModels();
      });
    }

    if (this.brandsEl) {
      this.brandsEl.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-brand-filter]');
        if (!btn) return;
        this.brandFilter = btn.dataset.brandFilter;
        this.brandsEl.querySelectorAll('[data-brand-filter]').forEach((b) => {
          b.classList.toggle('is-active', b === btn);
        });
        this.filterModels();
      });
    }

    this.addEventListener('click', (e) => {
      const model = e.target.closest('[data-model]');
      if (model && model.tagName === 'BUTTON') {
        this.chooseModel(model.dataset.model, model.dataset.modelName);
        return;
      }
      if (e.target.closest('[data-change-model]')) { this.backToModels(); return; }

      const remove = e.target.closest('[data-remove]');
      if (remove) {
        this.picks.splice(parseInt(remove.dataset.remove, 10), 1);
        this.clearError();
        this.render();
        return;
      }
      const pick = e.target.closest('[data-pick]');
      if (pick) { this.toggle(pick.closest('[data-card]')); return; }
      if (e.target.closest('[data-submit]')) this.submit();
    });
  }

  isPicked(cardId) {
    return this.picks.some((p) => String(p.cardId) === String(cardId));
  }

  toggle(card) {
    if (!card) return;
    const cardId = card.dataset.cardId;

    if (this.isPicked(cardId)) {
      this.picks = this.picks.filter((p) => String(p.cardId) !== String(cardId));
      this.clearError();
      this.render();
      return;
    }

    if (card.dataset.unavailable === 'true' || !card.dataset.variantId) return;

    if (this.picks.length >= this.boxSize) {
      this.showError(`Your box holds ${this.boxSize}. Remove one to swap it out.`);
      return;
    }

    const { product, variant } = this.currentFor(card);
    if (!product || !variant) return;

    this.picks.push({
      cardId,
      variantId: variant.id,
      productId: product.id,
      title: product.title,
      cents: variant.cents,
      image: variant.image || product.image
    });

    this.clearError();
    this.render();
  }

  /* ---------- render ---------- */

  render() {
    const total = this.picks.length;
    const full = total === this.boxSize;

    this.cards.forEach((card) => {
      if (card.hidden) return;
      const picked = this.isPicked(card.dataset.cardId);
      card.dataset.selected = String(picked);
      card.dataset.boxfull = String(full && !picked);
      const btn = card.querySelector('[data-pick]');
      if (btn) btn.setAttribute('aria-pressed', String(picked));
    });

    this.renderTray();

    const subtotal = this.picks.reduce((s, p) => s + p.cents, 0);
    const discounted = Math.round(subtotal * (1 - this.discount / 100));

    if (this.statusEl) {
      this.statusEl.textContent = full ? 'Your box is ready' : `${this.boxSize - total} more to add`;
    }

    if (this.moneyEl) {
      this.moneyEl.hidden = total === 0;
      if (total > 0) {
        const show = this.discount > 0 && full;
        this.wasEl.textContent = show ? this.fmt(subtotal) : '';
        this.nowEl.textContent = this.fmt(show ? discounted : subtotal);
        this.saveEl.textContent = show ? `Save ${this.discount}%` : '';
        this.saveEl.hidden = !show;
      }
    }

    if (this.submitEl) this.submitEl.disabled = !full;
  }

  renderTray() {
    if (!this.trayEl) return;
    let html = '';
    for (let i = 0; i < this.boxSize; i++) {
      const pick = this.picks[i];
      if (pick) {
        html += `<div class="bb__slot bb__slot--filled">
          ${pick.image ? `<img src="${pick.image}" alt="" width="54" height="54" loading="lazy">` : ''}
          <button type="button" class="bb__slot-remove" data-remove="${i}" aria-label="Remove ${this.escape(pick.title)}">&times;</button>
        </div>`;
      } else {
        const next = i === this.picks.length;
        html += `<div class="bb__slot${next ? ' bb__slot--next' : ''}">${i + 1}</div>`;
      }
    }
    this.trayEl.innerHTML = html;
  }

  fmt(cents) { return this.money.format(cents / 100); }
  showError(m) { if (this.errorEl) { this.errorEl.textContent = m; this.errorEl.hidden = false; } }
  clearError() { if (this.errorEl) this.errorEl.hidden = true; }

  /* ---------- cart ---------- */

  async submit() {
    if (this.picks.length !== this.boxSize) return;

    const bundleId = this.uuid();
    const grouped = new Map();
    this.picks.forEach((p) => grouped.set(p.variantId, (grouped.get(p.variantId) || 0) + 1));

    const props = { _bundle_id: bundleId, Bundle: this.bundleName };
    if (this.modelName) props.Model = this.modelName;

    // Gift details ride on the first line only, so the cart shows them once
    // rather than repeating the same message on all six items.
    const gift = this.giftDetails();
    if (this.giftRequired && this.giftEnabled && !gift.message) {
      this.showError('Please add a gift message before adding to cart.');
      if (this.giftMsgEl) this.giftMsgEl.focus();
      return;
    }

    const items = Array.from(grouped, ([id, quantity], i) => {
      const properties = Object.assign({}, props);
      if (i === 0) Object.assign(properties, this.giftProperties(gift));
      return { id, quantity, properties };
    });

    this.submitEl.disabled = true;
    const label = this.submitLabelEl.textContent;
    this.submitLabelEl.textContent = 'Adding…';
    this.clearError();

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || 'That did not go through. Try again.');
      }

      this.picks = [];
      this.clearGift();
      document.dispatchEvent(new CustomEvent('bundle:added', { detail: { bundleId, items } }));

      if (this.goToCart) { window.location.href = this.cartUrl; return; }

      this.render();
      this.submitLabelEl.textContent = 'Added to cart';
      setTimeout(() => { this.submitLabelEl.textContent = label; this.render(); }, 2000);
    } catch (e) {
      this.showError(e.message);
      this.submitEl.disabled = false;
      this.submitLabelEl.textContent = label;
    }
  }

  giftDetails() {
    const val = (el) => (el && el.value ? el.value.trim() : '');
    return {
      to: val(this.giftToEl),
      from: val(this.giftFromEl),
      message: val(this.giftMsgEl).slice(0, this.giftMax)
    };
  }

  /** Shape the gift details for the cart: one line, three lines, or hidden. */
  giftProperties(gift) {
    if (!gift.to && !gift.from && !gift.message) return {};

    if (this.giftDisplay === 'hidden') {
      const out = {};
      if (gift.to) out._gift_to = gift.to;
      if (gift.from) out._gift_from = gift.from;
      if (gift.message) out._gift_message = gift.message;
      return out;
    }

    if (this.giftDisplay === 'separate') {
      const out = {};
      if (gift.to) out['Gift to'] = gift.to;
      if (gift.from) out['Gift from'] = gift.from;
      if (gift.message) out['Gift message'] = gift.message;
      return out;
    }

    // single: one readable line, e.g. To Karthi, from Praveen — "Happy Raksha Bandhan"
    const bits = [];
    if (gift.to) bits.push(`To ${gift.to}`);
    if (gift.from) bits.push(`from ${gift.from}`);
    let line = bits.join(', ');
    if (gift.message) line = line ? `${line} — “${gift.message}”` : `“${gift.message}”`;
    return { [this.giftLabel]: line };
  }

  clearGift() {
    [this.giftToEl, this.giftFromEl, this.giftMsgEl].forEach((el) => { if (el) el.value = ''; });
    if (this.giftCountEl) this.giftCountEl.textContent = '0';
  }

  uuid() {
    if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
    return 'bb-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
  }

  escape(s) {
    return String(s).replace(/[&<>"']/g, (c) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[c]));
  }
}

if (!customElements.get('bundle-builder')) {
  customElements.define('bundle-builder', BundleBuilder);
}